/**
 * EmptyState / ErrorState — design system §3 + Non-HR UX (HUMANAI_UX_NONHR.md).
 *
 * EmptyState "PlainEmptyState": ตอนไม่มีข้อมูล อย่าทิ้งหน้าว่าง — บอกว่า
 * "เริ่มยังไง" (หลักการ 6) ด้วย CTA เริ่มต้น. แยกจาก loading/error ชัดเจน
 * (design system §4 ข้อ 5: count ต้องแยก 4 สถานะ).
 *
 * ErrorState: โหลดพลาด → มีปุ่มลองใหม่เสมอ.
 *
 * Portable — CSS class กลาง + lucide เท่านั้น (reuse ข้าม product).
 */
import type { ComponentType, ReactNode } from 'react';
import { Inbox, AlertCircle } from 'lucide-react';

/** ไม่มีข้อมูล — บอกทางไปต่อ ไม่ใช่แค่ "ไม่มี" */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  detail,
  action,
}: {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  detail?: ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex flex-col items-center text-center py-10 px-4">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <Icon className="w-6 h-6 text-gray-400" />
      </div>
      <p className="text-sm font-medium text-gray-900">{title}</p>
      {detail && <p className="text-sm text-gray-500 mt-1 max-w-xs">{detail}</p>}
      {action && (
        <button type="button" onClick={action.onClick} className="btn-cta min-h-11 mt-4">
          {action.label}
        </button>
      )}
    </div>
  );
}

/** โหลดพลาด — แยกจาก empty + มีปุ่มลองใหม่ */
export function ErrorState({
  title = 'โหลดข้อมูลไม่สำเร็จ',
  detail,
  onRetry,
}: {
  title?: string;
  detail?: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center text-center py-10 px-4">
      <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
        <AlertCircle className="w-6 h-6 text-red-500" />
      </div>
      <p className="text-sm font-medium text-gray-900">{title}</p>
      {detail && <p className="text-sm text-gray-500 mt-1 max-w-xs">{detail}</p>}
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn-secondary min-h-11 mt-4">
          ลองใหม่
        </button>
      )}
    </div>
  );
}
